import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useOrgStore } from '@/lib/store'

import type { CategoryTagStatus, MarketplaceSource, ScoutFilters } from './api'
import {
  addComment,
  deleteComment,
  fetchCategoryAccess,
  fetchCategoryProducts,
  fetchComments,
  fetchTree,
  searchCategories,
  setCategoryTag,
} from './api'

// Query keys are scoped by active org (and source) so cache never bleeds across orgs.
const keys = {
  access: (org: string | null) => ['categories', 'access', org] as const,
  tree: (org: string | null, source: string, parent: string) =>
    ['categories', 'tree', org, source, parent] as const,
  search: (org: string | null, source: string, q: string, status: string, owned: boolean) =>
    ['categories', 'search', org, source, q, status, owned] as const,
  comments: (org: string | null, source: string, externalId: string) =>
    ['categories', 'comments', org, source, externalId] as const,
}

export function useCategoryTreeAccess() {
  const org = useOrgStore((s) => s.activeOrgId)
  return useQuery({
    queryKey: keys.access(org),
    queryFn: fetchCategoryAccess,
    staleTime: 10 * 60 * 1000,
  })
}

export function useCategoryProducts(source: MarketplaceSource, externalId: string | null, filters: ScoutFilters = {}) {
  const org = useOrgStore((s) => s.activeOrgId)
  return useInfiniteQuery({
    queryKey: ['categories', 'products-infinite', org, source, externalId, filters],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchCategoryProducts(source, externalId, { ...filters, page: pageParam }),
    getNextPageParam: (lastPage) => lastPage.has_next ? lastPage.page + 1 : undefined,
    enabled: Boolean(org),
  })
}

export function useCategoryChildren(
  source: MarketplaceSource,
  parent: string | null,
  enabled = true,
) {
  const org = useOrgStore((s) => s.activeOrgId)
  return useQuery({
    queryKey: keys.tree(org, source, parent ?? ''),
    queryFn: () => fetchTree(source, parent),
    enabled: enabled && Boolean(org),
  })
}

export function useCategorySearch(
  source: MarketplaceSource,
  q: string,
  filters: { status?: CategoryTagStatus | ''; owned?: boolean },
) {
  const org = useOrgStore((s) => s.activeOrgId)
  const term = q.trim()
  const hasFilter = Boolean(filters.status) || Boolean(filters.owned)
  return useQuery({
    queryKey: keys.search(org, source, term, filters.status ?? '', Boolean(filters.owned)),
    queryFn: () => searchCategories(source, term, filters),
    // A term OR an active status/owned filter drives the results list; empty+no-filter = browse tree.
    enabled: Boolean(org) && (term.length > 0 || hasFilter),
  })
}

export function useCategoryComments(source: MarketplaceSource, externalId: string | null) {
  const org = useOrgStore((s) => s.activeOrgId)
  return useQuery({
    queryKey: keys.comments(org, source, externalId ?? ''),
    queryFn: () => fetchComments(source, externalId as string),
    enabled: Boolean(org) && Boolean(externalId),
  })
}

/** Invalidate every category query for the active org so tree/search reflect a mutation. */
function useInvalidateCategories() {
  const qc = useQueryClient()
  const org = useOrgStore((s) => s.activeOrgId)
  return () => qc.invalidateQueries({ queryKey: ['categories'], predicate: (query) => query.queryKey[2] === org })
}

export function useSetCategoryTag(source: MarketplaceSource) {
  const invalidate = useInvalidateCategories()
  return useMutation({
    mutationFn: ({ externalId, status }: { externalId: string; status: CategoryTagStatus | null }) =>
      setCategoryTag(source, externalId, status),
    onSuccess: invalidate,
  })
}

export function useAddComment(source: MarketplaceSource) {
  const invalidate = useInvalidateCategories()
  return useMutation({
    mutationFn: ({ externalId, body }: { externalId: string; body: string }) =>
      addComment(source, externalId, body),
    onSuccess: invalidate,
  })
}

export function useDeleteComment() {
  const invalidate = useInvalidateCategories()
  return useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: invalidate,
  })
}
